/**
 * Compact dropdown selector for switching between analysis versions
 * Used in the CreativeDetail header area
 */

import { useState, useRef, useEffect } from 'react';

import { decisionColors } from '@/lib/design-tokens';
import type { Analysis } from '@/types';

export interface AnalysisVersionSelectorProps {
  analyses: Analysis[];
  currentAnalysisId?: string;
  onSelectAnalysis: (analysisId: string) => void;
  isLoading?: boolean;
}

export function AnalysisVersionSelector({
  analyses,
  currentAnalysisId,
  onSelectAnalysis,
  isLoading = false,
}: AnalysisVersionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentAnalysis = analyses.find((a) => a.analysisId === currentAnalysisId);
  const hasMultipleVersions = analyses.length > 1;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (analyses.length === 0) {
    return null;
  }

  const handleSelect = (analysisId: string) => {
    onSelectAnalysis(analysisId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => hasMultipleVersions && setIsOpen(!isOpen)}
        disabled={!hasMultipleVersions || isLoading}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
          hasMultipleVersions
            ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
            : 'border-gray-200 cursor-default'
        } ${isLoading ? 'opacity-50' : ''}`}
      >
        {isLoading ? (
          <span className="text-sm text-gray-500">Loading...</span>
        ) : currentAnalysis ? (
          <>
            <span className="text-sm font-medium text-gray-700">
              v{currentAnalysis.version}
            </span>
            <span className="text-xs text-gray-500">
              ({currentAnalysis.templateSnapshot?.templateName || 'Standard'})
            </span>
            {currentAnalysis.status === 'complete' && currentAnalysis.result?.decision && (
              <span
                className="px-1.5 py-0.5 text-xs font-semibold uppercase rounded"
                style={{
                  backgroundColor: decisionColors[currentAnalysis.result.decision.call].bg,
                  color: decisionColors[currentAnalysis.result.decision.call].text,
                }}
              >
                {currentAnalysis.result.decision.call}
              </span>
            )}
            {hasMultipleVersions && (
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            )}
          </>
        ) : (
          <span className="text-sm text-gray-500">Select version</span>
        )}
      </button>

      {isOpen && hasMultipleVersions && (
        <div className="absolute z-20 mt-1 w-64 bg-surface border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {analyses.length} Version{analyses.length !== 1 ? 's' : ''}
            </span>
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {analyses.map((analysis) => {
              const isSelected = analysis.analysisId === currentAnalysisId;
              const isComplete = analysis.status === 'complete';
              const isFailed = analysis.status === 'failed';
              const isProcessing =
                analysis.status === 'processing' || analysis.status === 'queued';

              return (
                <li key={analysis.analysisId}>
                  <button
                    onClick={() => handleSelect(analysis.analysisId)}
                    disabled={isProcessing}
                    className={`w-full px-3 py-2 text-left transition-colors ${
                      isSelected
                        ? 'bg-blue-50'
                        : isProcessing
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          v{analysis.version}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({analysis.templateSnapshot?.templateName || 'Standard'})
                        </span>
                        {isComplete && analysis.result?.decision && (
                          <span
                            className="px-1.5 py-0.5 text-xs font-semibold uppercase rounded"
                            style={{
                              backgroundColor:
                                decisionColors[analysis.result.decision.call].bg,
                              color: decisionColors[analysis.result.decision.call].text,
                            }}
                          >
                            {analysis.result.decision.call}
                          </span>
                        )}
                        {isFailed && (
                          <span className="px-1.5 py-0.5 text-xs font-semibold uppercase rounded bg-red-100 text-red-700">
                            Failed
                          </span>
                        )}
                        {isProcessing && (
                          <span className="px-1.5 py-0.5 text-xs font-semibold uppercase rounded bg-yellow-100 text-yellow-700">
                            Processing
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {formatShortDate(analysis.requestedAt)}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
